import type { Dosha, MealType, Recipe, Season, ShoppingSelection } from "./types";

export const COACH_STORAGE_KEYS = {
  location: "ayurvedon.coach.location.v1",
  routine: "ayurvedon.coach.routine.v1",
  checkins: "ayurvedon.coach.checkins.v1",
  pantry: "ayurvedon.coach.pantry.v1",
  week: "ayurvedon.coach.week.v1",
} as const;

export function clearCoachData(): void {
  Object.values(COACH_STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
}

export interface SavedLocation {
  name: string;
  country?: string;
  latitude: number;
  longitude: number;
  timezone?: string;
}

export interface WeatherSnapshot {
  temperature: number;
  apparentTemperature: number;
  humidity: number;
  precipitation: number;
  windSpeed: number;
  weatherCode: number;
  temperatureMax: number;
  temperatureMin: number;
}

export interface RoutineState {
  enabled: string[];
  history: Record<string, string[]>;
}

export type CheckinSignal = "tired" | "irritated" | "bloated" | "cold" | "drySkin" | "cravings" | "poorSleep";

export interface CheckinEntry {
  date: string;
  signals: CheckinSignal[];
}

export interface CheckinTrend {
  dosha: Dosha;
  consecutiveDays: number;
  observations: number;
}

export interface WeekPlanEntry {
  date: string;
  meal: MealType;
  recipeSlug: string;
}

export interface WeekPlanState {
  startDate: string;
  people: number;
  entries: WeekPlanEntry[];
  checked: string[];
}

const MEAL_ORDER: Record<MealType, number> = { breakfast: 0, lunch: 1, dinner: 2 };

export const CHECKIN_SIGNALS: { id: CheckinSignal; labelKey: string; fallback: string; doshas: Dosha[] }[] = [
  { id: "tired", labelKey: "coach.checkin.signal.tired", fallback: "Müde", doshas: ["kapha"] },
  { id: "irritated", labelKey: "coach.checkin.signal.irritated", fallback: "Gereizt", doshas: ["pitta"] },
  { id: "bloated", labelKey: "coach.checkin.signal.bloated", fallback: "Aufgebläht", doshas: ["vata"] },
  { id: "cold", labelKey: "coach.checkin.signal.cold", fallback: "Mir ist kalt", doshas: ["vata"] },
  { id: "drySkin", labelKey: "coach.checkin.signal.drySkin", fallback: "Trockene Haut", doshas: ["vata"] },
  { id: "cravings", labelKey: "coach.checkin.signal.cravings", fallback: "Heißhunger", doshas: ["pitta", "kapha"] },
  { id: "poorSleep", labelKey: "coach.checkin.signal.poorSleep", fallback: "Schlechter Schlaf", doshas: ["vata"] },
];

function readJson<T>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) as T : fallback;
  } catch {
    return fallback;
  }
}

function saveJson(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export function formatDateKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function shiftDate(dateKey: string, days: number): string {
  const date = new Date(`${dateKey}T12:00:00`);
  date.setDate(date.getDate() + days);
  return formatDateKey(date);
}

export function getSeason(date = new Date(), southernHemisphere = false): Season {
  const month = (date.getMonth() + (southernHemisphere ? 6 : 0)) % 12;
  if (month >= 2 && month <= 4) return "spring";
  if (month >= 5 && month <= 7) return "summer";
  if (month >= 8 && month <= 10) return "autumn";
  return "winter";
}

export function weatherDosha(weather: WeatherSnapshot | null, fallback: Dosha): Dosha {
  if (!weather) return fallback;
  const scores: Record<Dosha, number> = { vata: 0, pitta: 0, kapha: 0 };
  if (weather.temperature >= 26 || weather.apparentTemperature >= 27) scores.pitta += 3;
  if (weather.temperature <= 8) scores.vata += 2;
  if (weather.windSpeed >= 25) scores.vata += 3;
  if (weather.humidity <= 38) scores.vata += 2;
  if (weather.humidity >= 78) scores.kapha += 2;
  if (weather.precipitation >= 2) scores.kapha += 2;
  const best = (Object.keys(scores) as Dosha[]).sort((left, right) => scores[right] - scores[left])[0];
  return scores[best] > 0 ? best : fallback;
}

function stableIndex(seed: string, length: number): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) hash = Math.imul(hash ^ seed.charCodeAt(index), 16777619);
  return length ? Math.abs(hash) % length : 0;
}

function seasonLabel(season: Season): string {
  return { spring: "Frühling", summer: "Sommer", autumn: "Herbst", winter: "Winter" }[season];
}

export function mealCandidates(recipes: Recipe[], meal: MealType): Recipe[] {
  const explicit = recipes.filter((recipe) => recipe.mealTypes?.includes(meal));
  if (explicit.length) return explicit;
  if (meal === "breakfast") return recipes.filter((recipe) => recipe.tags.includes("Frühstück"));
  return recipes.filter((recipe) => !recipe.tags.includes("Frühstück") && !recipe.tags.includes("Dessert"));
}

function rankedCandidates(recipes: Recipe[], meal: MealType, doshaInput: Dosha | Dosha[] | null, season: Season, pantry: Set<string>): Recipe[] {
  const doshas = doshaInput ? (Array.isArray(doshaInput) ? doshaInput : [doshaInput]) : [];
  return mealCandidates(recipes, meal).map((recipe) => {
    const ingredients = recipe.shoppingIngredients ?? [];
    const pantryHits = ingredients.filter((ingredient) => pantry.has(ingredient.key)).length;
    const pantryRatio = ingredients.length ? pantryHits / ingredients.length : 0;
    const doshaScore = doshas.reduce((score, dosha, index) => score + (recipe.doshas.includes(dosha) ? Math.max(2, 5 - index * 2) : 0), 0);
    const score = doshaScore
      + (recipe.season.includes(seasonLabel(season)) || recipe.season === "Ganzjährig" ? 3 : 0)
      + pantryRatio * 4;
    return { recipe, score };
  }).sort((left, right) => right.score - left.score || left.recipe.title.localeCompare(right.recipe.title, "de"))
    .map(({ recipe }) => recipe);
}

export function dailyMeals(recipes: Recipe[], dateKey: string, dosha: Dosha | Dosha[] | null, season: Season, pantry: Set<string>): Record<MealType, Recipe | null> {
  return Object.fromEntries((["breakfast", "lunch", "dinner"] as MealType[]).map((meal) => {
    const candidates = rankedCandidates(recipes, meal, dosha, season, pantry);
    return [meal, candidates[stableIndex(`${dateKey}:${meal}`, Math.min(candidates.length, 4))] ?? null];
  })) as Record<MealType, Recipe | null>;
}

export function loadLocation(): SavedLocation | null {
  return readJson<SavedLocation | null>(COACH_STORAGE_KEYS.location, null);
}

export function saveLocation(location: SavedLocation | null): void {
  if (location) saveJson(COACH_STORAGE_KEYS.location, location);
  else localStorage.removeItem(COACH_STORAGE_KEYS.location);
}

export function loadRoutineState(defaultIds: string[]): RoutineState {
  const stored = readJson<RoutineState>(COACH_STORAGE_KEYS.routine, { enabled: defaultIds, history: {} });
  return { enabled: Array.isArray(stored.enabled) ? stored.enabled : defaultIds, history: stored.history && typeof stored.history === "object" ? stored.history : {} };
}

export function saveRoutineState(state: RoutineState): void {
  saveJson(COACH_STORAGE_KEYS.routine, state);
}

export function routineStreak(state: RoutineState, today = formatDateKey()): number {
  if (!state.enabled.length) return 0;
  const complete = (date: string) => state.enabled.every((id) => state.history[date]?.includes(id));
  let cursor = complete(today) ? today : shiftDate(today, -1);
  let streak = 0;
  while (complete(cursor)) {
    streak += 1;
    cursor = shiftDate(cursor, -1);
  }
  return streak;
}

export function loadCheckins(): CheckinEntry[] {
  return readJson<CheckinEntry[]>(COACH_STORAGE_KEYS.checkins, []).filter((entry) => typeof entry.date === "string" && Array.isArray(entry.signals)).slice(-90);
}

export function saveCheckin(entry: CheckinEntry): CheckinEntry[] {
  const entries = loadCheckins().filter((item) => item.date !== entry.date);
  const next = [...entries, entry].sort((left, right) => left.date.localeCompare(right.date)).slice(-90);
  saveJson(COACH_STORAGE_KEYS.checkins, next);
  return next;
}

function dominantSignalDosha(entry: CheckinEntry): Dosha | null {
  const scores: Record<Dosha, number> = { vata: 0, pitta: 0, kapha: 0 };
  entry.signals.forEach((signal) => CHECKIN_SIGNALS.find((item) => item.id === signal)?.doshas.forEach((dosha) => { scores[dosha] += 1; }));
  const ordered = (Object.keys(scores) as Dosha[]).sort((left, right) => scores[right] - scores[left]);
  return scores[ordered[0]] > scores[ordered[1]] ? ordered[0] : null;
}

export function checkinTrend(entries: CheckinEntry[], today = formatDateKey()): CheckinTrend | null {
  const recent = entries.filter((entry) => entry.date >= shiftDate(today, -6) && entry.date <= today);
  if (recent.length < 3) return null;
  const observations: Record<Dosha, number> = { vata: 0, pitta: 0, kapha: 0 };
  recent.forEach((entry) => entry.signals.forEach((signal) => CHECKIN_SIGNALS.find((item) => item.id === signal)?.doshas.forEach((dosha) => { observations[dosha] += 1; })));
  const dosha = (Object.keys(observations) as Dosha[]).sort((left, right) => observations[right] - observations[left])[0];
  let consecutiveDays = 0;
  let cursor = today;
  const byDate = new Map(recent.map((entry) => [entry.date, dominantSignalDosha(entry)]));
  while (byDate.get(cursor) === dosha) {
    consecutiveDays += 1;
    cursor = shiftDate(cursor, -1);
  }
  return observations[dosha] > 0 ? { dosha, consecutiveDays, observations: observations[dosha] } : null;
}

export function loadPantry(): string[] {
  return readJson<string[]>(COACH_STORAGE_KEYS.pantry, []).filter((item) => typeof item === "string");
}

export function savePantry(keys: string[]): void {
  saveJson(COACH_STORAGE_KEYS.pantry, [...new Set(keys)].sort());
  window.dispatchEvent(new CustomEvent("ayurvedon:pantry-changed"));
}

export function pantryCoverage(recipe: Recipe, pantry: Set<string>): number {
  const ingredients = recipe.shoppingIngredients ?? [];
  return ingredients.length ? ingredients.filter((ingredient) => pantry.has(ingredient.key)).length / ingredients.length : 0;
}

export function generateWeekPlan(recipes: Recipe[], startDate: string, people: number, dosha: Dosha | Dosha[] | null, season: Season, pantry: Set<string>): WeekPlanState {
  const entries: WeekPlanEntry[] = [];
  (["breakfast", "lunch", "dinner"] as MealType[]).forEach((meal) => {
    const candidates = rankedCandidates(recipes, meal, dosha, season, pantry);
    const mealOffset = meal === "dinner" && candidates.length > 1 ? Math.ceil(candidates.length / 2) : 0;
    for (let day = 0; day < 7; day += 1) {
      const recipe = candidates[(stableIndex(startDate, Math.max(1, candidates.length)) + day + mealOffset) % Math.max(1, candidates.length)];
      if (recipe) entries.push({ date: shiftDate(startDate, day), meal, recipeSlug: recipe.slug });
    }
  });
  return { startDate, people, entries: entries.sort((left, right) => left.date.localeCompare(right.date) || MEAL_ORDER[left.meal] - MEAL_ORDER[right.meal]), checked: [] };
}

export function loadWeekPlan(): WeekPlanState | null {
  return readJson<WeekPlanState | null>(COACH_STORAGE_KEYS.week, null);
}

export function saveWeekPlan(plan: WeekPlanState): void {
  saveJson(COACH_STORAGE_KEYS.week, plan);
}

export function weekShoppingSelections(plan: WeekPlanState): ShoppingSelection[] {
  return plan.entries.map((entry) => ({ recipeSlug: entry.recipeSlug, people: plan.people }));
}

export function currentWeekStart(date = new Date()): string {
  const copy = new Date(date);
  const weekday = copy.getDay() || 7;
  copy.setDate(copy.getDate() - weekday + 1);
  return formatDateKey(copy);
}
